import {
  DIFFICULTY_MAP,
  LESSON_TYPE_MAP,
  FALLBACK_OBJECTIVES,
  FALLBACK_ACTIVITIES,
  FALLBACK_MATERIALS,
} from "./constants";
import type { FormData, GeneratedLesson, BNCCInfo } from "./types";

export function validateFormData(formData: FormData): string | null {
  if (!formData.subject?.trim()) {
    return "Por favor, selecione uma matéria!";
  }
  if (!formData.grade?.trim()) {
    return "Por favor, selecione uma série/turma!";
  }
  if (!formData.topic?.trim()) {
    return "Por favor, informe o tópico principal!";
  }
  return null;
}

export function mapErrorToUserMessage(error: Error): string {
  const message = error.message;

  if (message.includes("Failed to fetch")) {
    return "Não foi possível conectar ao servidor. Verifique se a API está rodando na porta 8000.";
  }

  if (message.includes("Timeout")) {
    return "A requisição demorou muito. Tente novamente.";
  }

  if (message.includes("detail")) {
    return message;
  }

  return "Erro desconhecido ao gerar aula";
}

export function validateAPIResponse(response: any): void {
  if (!response.success) {
    throw new Error(response.error || "Erro ao gerar plano de aula");
  }

  if (!response.data) {
    throw new Error("Erro ao gerar plano de aula");
  }
}

export function sanitizeFileName(filename: string): string {
  return filename
    .replace(/[^a-z0-9]/gi, "_")
    .toLowerCase()
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

export function extractObjectives(n8nData: any, formData: FormData): string[] {
  const planoAula = n8nData.plano_aula || {};
  let objectives: string[] = [];

  if (
    planoAula.objetivos_especificos &&
    Array.isArray(planoAula.objetivos_especificos)
  ) {
    objectives = planoAula.objetivos_especificos
      .map((obj: string) => obj.replace(/^- /, ""))
      .slice(0, 4);
  }

  if (objectives.length === 0 && planoAula.objetivo_geral) {
    objectives = [planoAula.objetivo_geral];
  }

  if (objectives.length === 0 && formData.objectives) {
    objectives = formData.objectives
      .split("\n")
      .filter((o: string) => o.trim());
  }

  if (objectives.length === 0) {
    objectives = FALLBACK_OBJECTIVES;
  }

  return objectives;
}

export function buildLessonPlan(
  n8nData: any,
  formData: FormData,
): Record<string, any> {
  const planoAula = n8nData.plano_aula || {};
  const lessonPlan: any = {};

  if (planoAula.desenvolvimento_aula?.aula_1?.[0]) {
    const intro = planoAula.desenvolvimento_aula.aula_1[0];
    lessonPlan.introduction = {
      title: "Introdução (10-15 minutos)",
      description:
        intro.descricao ||
        intro.momento ||
        "Apresentação do tema e ativação de conhecimentos prévios.",
      activities: [
        intro.momento || "Introdução ao tema",
        "Apresentação dos objetivos da aula",
        "Discussão inicial sobre o tema",
      ],
    };
  } else {
    lessonPlan.introduction = {
      title: "Introdução (10-15 minutos)",
      description: "Apresentação do tema e ativação de conhecimentos prévios.",
      activities: [
        "Levantamento de conhecimento prévio",
        "Contextualização do tema",
        "Apresentação dos objetivos",
      ],
    };
  }

  const desenvolvimentoAtividades: string[] = [];
  if (planoAula.desenvolvimento_aula?.aula_1?.[1]) {
    desenvolvimentoAtividades.push(
      planoAula.desenvolvimento_aula.aula_1[1].descricao ||
        planoAula.desenvolvimento_aula.aula_1[1].momento,
    );
  }

  if (
    planoAula.conteudos_programaticos &&
    Array.isArray(planoAula.conteudos_programaticos)
  ) {
    const conteudos = planoAula.conteudos_programaticos.slice(0, 2);
    conteudos.forEach((conteudo: string) => {
      desenvolvimentoAtividades.push(conteudo.replace(/^- /, ""));
    });
  }

  lessonPlan.development = {
    title: `Desenvolvimento (${formData.duration === "60" ? "25" : "35"} minutos)`,
    description:
      "Exposição dos conceitos principais com exemplos práticos e atividades interativas.",
    activities:
      desenvolvimentoAtividades.length > 0
        ? desenvolvimentoAtividades
        : FALLBACK_ACTIVITIES,
  };

  const praticaAtividades: string[] = [];
  if (planoAula.desenvolvimento_aula?.aula_2?.[1]) {
    praticaAtividades.push(
      planoAula.desenvolvimento_aula.aula_2[1].descricao ||
        planoAula.desenvolvimento_aula.aula_2[1].momento,
    );
  }

  if (planoAula.atividade_proposta) {
    praticaAtividades.push(planoAula.atividade_proposta);
  }

  lessonPlan.practice = {
    title: "Prática (15-20 minutos)",
    description:
      "Aplicação dos conceitos aprendidos em atividades práticas e exercícios.",
    activities:
      praticaAtividades.length > 0
        ? praticaAtividades
        : [
            "Exercícios individuais",
            "Trabalho em duplas",
            "Resolução de problemas",
            "Aplicação em contexto real",
          ],
  };

  lessonPlan.conclusion = {
    title: "Conclusão (5-10 minutos)",
    description:
      "Síntese dos pontos principais, verificação da aprendizagem e feedback.",
    activities: [
      "Revisão dos conceitos aprendidos",
      "Avaliação formativa rápida",
      "Próximos passos e tarefa para casa",
    ],
  };

  return lessonPlan;
}

export function extractActivities(n8nData: any): any[] {
  const planoAula = n8nData.plano_aula || {};
  const activities: any[] = [];

  if (planoAula.desenvolvimento_aula?.aula_1) {
    planoAula.desenvolvimento_aula.aula_1.forEach(
      (item: any, index: number) => {
        activities.push({
          title: item.momento || `Atividade ${index + 1}`,
          description: item.descricao || "Descrição da atividade",
          duration: 15,
          type: "conceptual",
        });
      },
    );
  }

  if (planoAula.desenvolvimento_aula?.aula_2) {
    planoAula.desenvolvimento_aula.aula_2.forEach(
      (item: any, index: number) => {
        activities.push({
          title: item.momento || `Atividade ${index + 3}`,
          description: item.descricao || "Descrição da atividade",
          duration: 20,
          type: "practice",
        });
      },
    );
  }

  if (activities.length === 0) {
    activities.push(
      {
        title: "Análise de Textos",
        description: "Identificação de conceitos em exemplos",
        duration: 15,
        type: "analysis",
      },
      {
        title: "Exercícios Práticos",
        description: "Aplicação dos conceitos aprendidos",
        duration: 20,
        type: "practice",
      },
      {
        title: "Discussão em Grupo",
        description: "Debate e reflexão sobre o tema",
        duration: 25,
        type: "collaborative",
      },
    );
  }

  return activities;
}

export function extractMaterials(n8nData: any): string[] {
  const planoAula = n8nData.plano_aula || {};
  const materials: string[] = [];

  if (
    planoAula.recursos_didaticos &&
    Array.isArray(planoAula.recursos_didaticos)
  ) {
    planoAula.recursos_didaticos.forEach((recurso: string) => {
      if (typeof recurso === "string") {
        materials.push(recurso.replace(/^- /, ""));
      }
    });
  }

  return materials.length > 0 ? materials : FALLBACK_MATERIALS;
}

export function extractAssessment(n8nData: any): any {
  const planoAula = n8nData.plano_aula || {};
  const assessment = {
    formative: ["Observação da participação", "Feedback durante as atividades"],
    summative: ["Tarefa prática", "Questionário de verificação"],
  };

  if (planoAula.avaliacao) {
    if (
      planoAula.avaliacao.formativa &&
      Array.isArray(planoAula.avaliacao.formativa)
    ) {
      assessment.formative = planoAula.avaliacao.formativa
        .map((item: string) => item.replace(/^- /, ""))
        .slice(0, 3);
    }

    if (
      planoAula.avaliacao.somativa &&
      Array.isArray(planoAula.avaliacao.somativa)
    ) {
      assessment.summative = planoAula.avaliacao.somativa
        .map((item: string) => item.replace(/^- /, ""))
        .slice(0, 2);
    }
  }

  return assessment;
}

export function extractBNCCInfo(n8nData: any): any {
  const planoAula = n8nData.plano_aula || {};
  const bnccInfo: BNCCInfo = {
    competencias: [],
    habilidades: [],
    objetosConhecimento: [],
  };

  if (planoAula.habilidades_bncc && Array.isArray(planoAula.habilidades_bncc)) {
    planoAula.habilidades_bncc.forEach((habilidade: any) => {
      if (habilidade.descricao) {
        bnccInfo.habilidades.push(habilidade.descricao);
      }
    });
  }

  return bnccInfo;
}

export function transformN8NResponse(
  n8nResponse: any,
  formData: FormData,
): GeneratedLesson {
  console.log("📊 Transformando resposta N8N:", n8nResponse);

  // Acessar o objeto plano_aula da resposta (mesma estrutura do CreateLessonPlanAI)
  const planoAula = n8nResponse.plano_aula || {};
  const identificacao = planoAula.identificacao || {};

  // Extrair informações básicas
  const disciplina = n8nResponse.disciplina || formData.subject;
  const serie = n8nResponse.serie || formData.grade;
  const tema = n8nResponse.tema || formData.topic;

  // Processar objetivos (mesma lógica do CreateLessonPlanAI)
  let objectives: string[] = [];

  if (
    planoAula.objetivos_especificos &&
    Array.isArray(planoAula.objetivos_especificos)
  ) {
    objectives = planoAula.objetivos_especificos
      .map((obj: string) => obj.replace(/^- /, "").trim())
      .filter(Boolean)
      .slice(0, 5);
  }

  if (objectives.length === 0 && planoAula.objetivo_geral) {
    objectives = [planoAula.objetivo_geral];
  }

  if (objectives.length === 0) {
    objectives = [
      "Compreender os conceitos fundamentais do tema",
      "Aplicar o conhecimento em situações práticas",
      "Desenvolver habilidades de análise crítica",
    ];
  }

  // Processar desenvolvimento da aula (momento_1, momento_2, etc.)
  const lessonPlan: any = {};
  const desenvolvimento = planoAula.desenvolvimento_aula || {};

  // Momento 1 - Introdução
  if (desenvolvimento.momento_1) {
    lessonPlan.introduction = {
      title: desenvolvimento.momento_1.titulo || "Introdução",
      time: desenvolvimento.momento_1.tempo || "15 minutos",
      description:
        desenvolvimento.momento_1.descricao || "Apresentação do tema",
      activities: [
        desenvolvimento.momento_1.descricao || "Introdução ao tema",
        "Ativação de conhecimentos prévios",
        "Apresentação dos objetivos",
      ],
    };
  }

  // Momento 2 - Desenvolvimento
  if (desenvolvimento.momento_2) {
    lessonPlan.development = {
      title: desenvolvimento.momento_2.titulo || "Desenvolvimento",
      time: desenvolvimento.momento_2.tempo || "25 minutos",
      description:
        desenvolvimento.momento_2.descricao || "Desenvolvimento dos conceitos",
      activities: [
        desenvolvimento.momento_2.descricao || "Explicação dos conceitos",
        "Exemplos práticos",
        "Discussão interativa",
      ],
    };
  }

  // Momento 3 - Prática
  if (desenvolvimento.momento_3) {
    lessonPlan.practice = {
      title: desenvolvimento.momento_3.titulo || "Prática",
      time: desenvolvimento.momento_3.tempo || "15 minutos",
      description: desenvolvimento.momento_3.descricao || "Aplicação prática",
      activities: [
        desenvolvimento.momento_3.descricao || "Exercícios práticos",
        "Trabalho em grupo",
        "Resolução de problemas",
      ],
    };
  }

  // Momento 4 - Encerramento
  if (desenvolvimento.momento_4) {
    lessonPlan.conclusion = {
      title: desenvolvimento.momento_4.titulo || "Encerramento",
      time: desenvolvimento.momento_4.tempo || "5 minutos",
      description:
        desenvolvimento.momento_4.descricao || "Síntese e fechamento",
      activities: [
        desenvolvimento.momento_4.descricao || "Revisão dos conceitos",
        "Avaliação formativa",
        "Próximos passos",
      ],
    };
  }

  // Processar atividades
  const activities: any[] = [];

  if (planoAula.atividade_proposta) {
    activities.push({
      title: "Atividade Principal",
      description: planoAula.atividade_proposta,
      duration: 20,
      type: "practice",
    });
  }

  if (
    planoAula.conteudos_programaticos &&
    Array.isArray(planoAula.conteudos_programaticos)
  ) {
    planoAula.conteudos_programaticos
      .slice(0, 3)
      .forEach((conteudo: string, index: number) => {
        activities.push({
          title: `Conteúdo ${index + 1}`,
          description: conteudo.replace(/^- /, ""),
          duration: 15,
          type: "conceptual",
        });
      });
  }

  // Garantir pelo menos 2 atividades
  if (activities.length === 0) {
    activities.push(
      {
        title: "Atividade Teórica",
        description: "Explicação e demonstração dos conceitos",
        duration: 20,
        type: "conceptual",
      },
      {
        title: "Atividade Prática",
        description: "Exercícios e aplicação do conhecimento",
        duration: 25,
        type: "practice",
      },
    );
  }

  // Processar materiais
  const materials: string[] = [];
  if (
    planoAula.recursos_didaticos &&
    Array.isArray(planoAula.recursos_didaticos)
  ) {
    planoAula.recursos_didaticos.forEach((recurso: string) => {
      if (typeof recurso === "string") {
        materials.push(recurso.replace(/^- /, "").trim());
      }
    });
  }

  if (materials.length === 0) {
    materials.push(
      "Projetor e computador",
      "Material didático impresso",
      "Quadro branco e marcadores",
      "Recursos multimídia",
    );
  }

  // Processar avaliação
  const assessment = {
    formative: ["Observação da participação", "Feedback durante atividades"],
    summative: ["Tarefa prática", "Avaliação escrita"],
  };

  if (planoAula.avaliacao) {
    if (
      planoAula.avaliacao.formativa &&
      Array.isArray(planoAula.avaliacao.formativa)
    ) {
      assessment.formative = planoAula.avaliacao.formativa
        .map((item: string) => item.replace(/^- /, "").trim())
        .filter(Boolean)
        .slice(0, 3);
    }

    if (
      planoAula.avaliacao.somativa &&
      Array.isArray(planoAula.avaliacao.somativa)
    ) {
      assessment.summative = planoAula.avaliacao.somativa
        .map((item: string) => item.replace(/^- /, "").trim())
        .filter(Boolean)
        .slice(0, 3);
    }
  }

  // Processar habilidades BNCC
  const bnccInfo: {
    competencias: string[];
    habilidades: string[];
    objetosConhecimento: string[];
  } = {
    competencias: [],
    habilidades: [],
    objetosConhecimento: [],
  };

  if (planoAula.habilidades_bncc && Array.isArray(planoAula.habilidades_bncc)) {
    planoAula.habilidades_bncc.forEach((habilidade: any) => {
      if (typeof habilidade === "object" && habilidade.descricao) {
        const codigo = habilidade.codigo || "";
        const descricao = habilidade.descricao || "";
        bnccInfo.habilidades.push(`${codigo}: ${descricao}`.trim());
      } else if (typeof habilidade === "string") {
        bnccInfo.habilidades.push(habilidade);
      }
    });
  }

  // Processar conteúdos programáticos para objetos de conhecimento
  if (
    planoAula.conteudos_programaticos &&
    Array.isArray(planoAula.conteudos_programaticos)
  ) {
    bnccInfo.objetosConhecimento = planoAula.conteudos_programaticos
      .map((c: string) => c.replace(/^- /, "").trim())
      .filter(Boolean);
  }

  // Recursos tecnológicos
  const techResources = [
    "Apresentação multimídia (PowerPoint/Google Slides)",
    "Vídeos educativos relacionados ao tema",
    "Ferramentas de avaliação online (Kahoot, Quizizz)",
    "Plataformas colaborativas",
  ];

  // Justificativa e metodologia
  const justification =
    planoAula.justificativa ||
    `Esta aula sobre ${tema} é fundamental para o desenvolvimento das competências previstas na BNCC para ${serie}.`;

  const methodology =
    planoAula.metodologia ||
    "Metodologia ativa com foco na participação dos alunos e construção coletiva do conhecimento.";

  // Referências
  const references = planoAula.referencias || [
    "BRASIL. Base Nacional Comum Curricular. Brasília: MEC, 2018.",
  ];

  // Retornar objeto formatado (compatível com GeneratedLesson)
  return {
    id: Date.now().toString(),
    title: formData.title || identificacao.tema || tema,
    subject: disciplina,
    grade: serie,
    topic: tema,
    objectives,
    duration: parseInt(formData.duration),
    studentsCount: formData.studentsCount || 0,
    difficulty: formData.difficulty,
    difficultyLabel: DIFFICULTY_MAP[formData.difficulty],
    lessonType: formData.lessonType,
    lessonTypeLabel: LESSON_TYPE_MAP[formData.lessonType],
    lessonPlan,
    activities,
    materials,
    assessment,
    differentiation: {
      forStruggling: planoAula.recuperacao_aprendizagem || [
        "Explicação individualizada",
        "Material de apoio simplificado",
        "Exercícios de reforço",
      ],
      forAdvanced: [
        "Desafios extras",
        "Pesquisa complementar",
        "Projetos de aprofundamento",
      ],
    },
    techResources,
    keywords: [disciplina, tema, serie].filter(Boolean),
    createdAt: n8nResponse.metadata?.generatedAt || new Date().toISOString(),
    aiGenerated: true,
    estimatedPreparationTime: "30-45 minutos",
    bnccInfo,
    rawN8NData: n8nResponse,
  };
}
